import type { AgentPresentationStep } from "./agent-presentation-types";
import type { PresentationRun } from "./agent-presentation-controller";
import type { PresentationOperations } from "./agent-presentation-operations";
import {
  failPresentationStep,
  acknowledgePresentationPaint,
} from "./agent-presentation-ack";
import {
  componentComplete,
  reportStepProgress,
} from "./presentation-step-progress";
function timingForStep(step: AgentPresentationStep): {
  transitionMs: number;
  holdMs: number;
} {
  if (step.kind === "pause") {
    return { transitionMs: 0, holdMs: step.duration_ms };
  }
  if (step.kind === "clear") return { transitionMs: 0, holdMs: 0 };
  return {
    transitionMs:
      step.kind === "frame" ? (step.transition?.duration_ms ?? 0) : 0,
    holdMs: step.hold_ms ?? step.duration_ms ?? 0,
  };
}

export async function performPresentationStep(
  run: PresentationRun,
  step: AgentPresentationStep,
  signal: AbortSignal,
  onVisible: () => void,
  operations: PresentationOperations,
  nextAction: () => number,
  replayCount = 0,
): Promise<boolean> {
  const presentation = { ...run.presentation, speechAttempt: replayCount };
  const receipt =
    replayCount === 0
      ? presentation.resumeReceipt?.find(
          (item) => item.step_id === step.step_id,
        )
      : undefined;
  if (
    componentComplete(receipt?.visual) &&
    !(await acknowledgePresentationPaint(run.presentation, step, signal))
  )
    return false;
  const visibleAt = Date.now();
  const rendering = componentComplete(receipt?.visual)
    ? Promise.resolve(true)
    : renderStep(run, step, signal, operations, nextAction);
  const safeRendering = rendering.catch(() => false);
  const narrated = componentComplete(receipt?.speech)
    ? reportStepProgress(
        presentation,
        step,
        "speech",
        receipt?.speech === "skipped" ? "skipped" : "finished",
      )
    : (operations.narrate?.(presentation, step, signal) ??
      Promise.resolve(true));
  const safeNarration = narrated.catch(() => false);
  const rendered = await safeRendering;

  if (!rendered) {
    if (!signal.aborted) {
      await failPresentationStep(
        run.presentation,
        step,
        "render_operation_failed",
      );
    }
    if (!signal.aborted) run.abortController.abort();
    await safeNarration;
    return false;
  }
  const timing = timingForStep(step);
  if (timing.transitionMs > 0)
    await operations.delay(timing.transitionMs, signal);
  if (signal.aborted) {
    await safeNarration;
    return false;
  }
  onVisible();
  run.lifecycle = "visible";
  if (!(await safeNarration)) return false;
  const remainingHoldMs = Math.max(0, timing.holdMs - (Date.now() - visibleAt));
  if (remainingHoldMs > 0) await operations.delay(remainingHoldMs, signal);
  if (signal.aborted) return false;
  if (
    step.kind === "click" ||
    step.kind === "hover" ||
    step.kind === "paste" ||
    step.kind === "drag" ||
    step.kind === "scroll" ||
    step.kind === "key" ||
    step.kind === "type"
  ) {
    let failureReason = "foreground_action_failed";
    const interacted = await operations.interact(
      run.presentation,
      step,
      signal,
      (reason) => {
        failureReason = `foreground_action_failed: ${reason}`;
      },
    );
    if (!interacted) {
      if (!signal.aborted) {
        await failPresentationStep(run.presentation, step, failureReason);
      }
      return false;
    }
  }
  if (step.kind === "pause" || step.kind === "clear") {
    const painted = await acknowledgePresentationPaint(
      run.presentation,
      step,
      signal,
    );
    if (!painted) {
      return false;
    }
  }
  if (!("user_action" in step && step.user_action)) {
    if (
      !(await reportStepProgress(
        run.presentation,
        step,
        "user_action",
        "skipped",
      ))
    )
      return false;
  }
  return !signal.aborted;
}

async function renderStep(
  run: PresentationRun,
  step: AgentPresentationStep,
  signal: AbortSignal,
  operations: PresentationOperations,
  nextAction: () => number,
): Promise<boolean> {
  if (
    step.kind === "point" ||
    step.kind === "highlight" ||
    step.kind === "draw" ||
    step.kind === "click" ||
    step.kind === "hover" ||
    step.kind === "paste" ||
    step.kind === "drag" ||
    step.kind === "scroll" ||
    step.kind === "key"
  ) {
    const actionSequence = nextAction();
    run.usedCursor = true;
    if (step.kind === "draw" || step.kind === "highlight")
      run.retainsCompletedAnnotation = true;
    return operations.showCursor(
      run.presentation,
      step,
      actionSequence,
      signal,
    );
  }
  if (step.kind === "frame") {
    run.usedCursor ||= Boolean(step.global_point || step.capture_point);
    return operations.showFrames(run.presentation, step, signal);
  }
  if (step.kind === "clear") {
    const targets = new Set(
      step.targets ?? ["cursor", "annotations", "frames"],
    );
    if (targets.has("cursor")) {
      operations.clearVisuals(run.presentation, "execution");
      run.usedCursor = false;
    }
    if (targets.has("annotations") && !targets.has("cursor")) {
      operations.clearAnnotations(run.presentation, "execution");
    }
    if (targets.has("annotations") || targets.has("cursor")) {
      run.retainsCompletedAnnotation = false;
    }
    if (targets.has("frames")) {
      operations.clearFrames(run.presentation);
    }
  }
  return true;
}
